import React, { useState, useEffect } from 'react';
import Wrapper from '../../Main/Universal/Wrapper';
import { formatDate, postAPI, postAPIForm } from '../../../app/api';
import Swal from 'sweetalert2';

export default function Members() {
    const [members, setMembers] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false); // Disabled - form moved to ManageMembers
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewMember, setViewMember] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        first_name: '',
        middle_name: '',
        last_name: '',
        designation: '',
        dob_of_spouse: '',
        email_id: '',
        mobile_number: '',
        landline_number: '',
        passing_year: '',
        discipline: '',
        blood_group: '',
        postal_address: '',
        pin_code: '',
        state: '',
        country: '',
        spouse_name: '',
        date_of_birth: '',
        introduce_by: '',
        introduce_passing_year: '',
        introduce_discipline: '',
        image: '',
        payment_type: 'Cash',
        cheque_number: '',
        offline_form: '',
        is_agree: true,
        status: 'active'
    });
    const [selectedImageFile, setSelectedImageFile] = useState(null);
    const [selectedFormFile, setSelectedFormFile] = useState(null);

    // Fetch all members
    useEffect(() => {
        fetchMembers();
    }, []);

    // Filter members by search term
    const filteredMembers = members.filter(member => {
        const term = searchTerm.toLowerCase();
        return (
            (member.first_name && member.first_name.toLowerCase().includes(term)) ||
            (member.last_name && member.last_name.toLowerCase().includes(term)) ||
            (member.email_id && member.email_id.toLowerCase().includes(term)) ||
            (member.mobile_number && member.mobile_number.toLowerCase().includes(term))
        );
    });

    const fetchMembers = async () => {
        try {
            setLoading(true);
            // Use finance/list to get members with payment data
            const response = await postAPI('finance/list', {});
            if (response?.status === 1) {
                // Deduplicate members by user_code to ensure each member appears only once
                const uniqueMembers = [];
                const seenUserCodes = new Set();

                for (const member of (response?.data || [])) {
                    // Include members who have made payment (submission_status = 'PAID')
                    // OR members added by admin (payment already done offline)
                    const isPaid = member.submission_status === 'PAID';
                    const isAdminAdded = member.added_by_admin === 1 || member.added_by_admin === '1';

                    if (!seenUserCodes.has(member.user_code) && (isPaid || isAdminAdded)) {
                        seenUserCodes.add(member.user_code);
                        uniqueMembers.push(member);
                    }
                }

                setMembers(uniqueMembers);
            }
        } catch (error) {
            console.error('Error fetching members:', error);
            Swal.fire('Error', 'Failed to fetch members', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Handle form changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle image file upload
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedImageFile(file);
            // Create preview URL
            setFormData(prev => ({
                ...prev,
                image: URL.createObjectURL(file)
            }));
        }
    };

    // Handle form file upload
    const handleFormFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFormFile(file);
            setFormData(prev => ({
                ...prev,
                offline_form: file.name
            }));
        }
    };

    // Add/Update member
    const handleAddMember = async (e) => {
        e.preventDefault();
        try {
            // Enhanced validation for complete form
            if (!formData.first_name || !formData.last_name || !formData.email_id || !formData.mobile_number ||
                !formData.passing_year || !formData.discipline || !formData.blood_group || !formData.date_of_birth ||
                !formData.introduce_by || !formData.introduce_passing_year || !formData.introduce_discipline) {
                Swal.fire('Validation', 'Please fill all required fields', 'warning');
                return;
            }

            // Validate payment type and cheque number
            if (formData.payment_type === 'Cheque' && !formData.cheque_number) {
                Swal.fire('Validation', 'Cheque number is required when payment type is Cheque', 'warning');
                return;
            }

            const submitData = {
                first_name: formData.first_name,
                middle_name: formData.middle_name,
                last_name: formData.last_name,
                designation: formData.designation,
                dob_of_spouse: formData.dob_of_spouse,
                email_id: formData.email_id,
                mobile_number: formData.mobile_number,
                landline_number: formData.landline_number,
                passing_year: formData.passing_year,
                discipline: formData.discipline,
                blood_group: formData.blood_group,
                postal_address: formData.postal_address,
                pin_code: formData.pin_code,
                state: formData.state,
                country: formData.country,
                spouse_name: formData.spouse_name,
                date_of_birth: formData.date_of_birth,
                introduce_by: formData.introduce_by,
                introduce_passing_year: formData.introduce_passing_year,
                introduce_discipline: formData.introduce_discipline,
                payment_type: formData.payment_type,
                cheque_number: formData.payment_type === 'Cheque' ? formData.cheque_number : '',
                image: formData.image,
                status: formData.status,
                is_agree: formData.is_agree,
                action: editingId ? 'UPDATE' : 'ADD'
            };

            // Upload image if a new file is selected
            if (selectedImageFile) {
                const imgFormData = new FormData();
                imgFormData.append('baseDir', 'members');
                imgFormData.append('filename', 'member_image');
                imgFormData.append('original_file', selectedImageFile);

                const imgResponse = await postAPIForm('file/uploadFile', imgFormData);
                if (imgResponse?.status === 1) {
                    submitData.image = imgResponse?.filepath;
                } else {
                    console.error('Image upload failed:', imgResponse);
                }
            }

            // Upload offline form if selected
            if (selectedFormFile) {
                const formFileData = new FormData();
                formFileData.append('baseDir', 'offline_forms');
                formFileData.append('filename', 'offline_form');
                formFileData.append('original_file', selectedFormFile);

                const formResponse = await postAPIForm('file/uploadFile', formFileData);
                if (formResponse?.status === 1) {
                    submitData.offline_form = formResponse?.filepath;
                } else {
                    console.error('Form file upload failed:', formResponse);
                }
            }

            let response;
            if (editingId) {
                submitData.id = editingId;
                response = await postAPI('authentication/users/update-profile', submitData);
            } else {
                response = await postAPI('authentication/users/add-member', submitData);
            }
            if (editingId) {
                submitData.id = editingId;
                response = await postAPI('authentication/users/update-profile', submitData);
            } else {
                response = await postAPI('authentication/users/add-member', submitData);
            }

            if (response?.status === 1) {
                Swal.fire('Success', 'Member saved successfully', 'success');
                resetForm();
                fetchMembers();
            } else {
                Swal.fire('Error', response?.msg || 'Failed to save member', 'error');
            }
        } catch (error) {
            console.error('Error saving member:', error);
            Swal.fire('Error', error.message || 'Failed to save member', 'error');
        }
    };

    // Delete member
    const handleApproveMember = async (member) => {
        const result = await Swal.fire({
            title: 'Confirm Approve',
            text: 'Are you sure you want to approve this member?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Approve',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                const response = await postAPI('authentication/users/approve', { user_code: member.user_code, approved_by: 'admin' });
                if (response?.status === 1) {
                    Swal.fire('Success', 'Member approved successfully', 'success');
                    fetchMembers();
                }
            } catch (error) {
                Swal.fire('Error', 'Failed to approve member', 'error');
            }
        }
    };

    // Delete member
    const handleDeleteMember = async (id) => {
        const result = await Swal.fire({
            title: 'Confirm Delete',
            text: 'Are you sure you want to delete this member?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                const response = await postAPI('authentication/users/delete', { id });
                if (response?.status === 1) {
                    Swal.fire('Success', 'Member deleted successfully', 'success');
                    fetchMembers();
                }
            } catch (error) {
                Swal.fire('Error', 'Failed to delete member', 'error');
            }
        }
    };

    // Decline member - permanently delete so they can re-register
    const handleDeclineMember = async (member) => {
        const result = await Swal.fire({
            title: 'Decline Member Application',
            html: `<p>Are you sure you want to decline <strong>${member.first_name} ${member.last_name}</strong>'s application?</p><p class="text-danger"><small>This will permanently delete their registration data (email & phone), allowing them to register again.</small></p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            confirmButtonText: 'Yes, Decline',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                const response = await postAPI('authentication/users/delete', { id: member.id });
                if (response?.status === 1) {
                    Swal.fire('Declined', 'Member application has been declined and data removed. They can now re-register.', 'success');
                    setShowViewModal(false);
                    fetchMembers();
                } else {
                    Swal.fire('Error', response?.msg || 'Failed to decline member', 'error');
                }
            } catch (error) {
                Swal.fire('Error', 'Failed to decline member', 'error');
            }
        }
    };

    const resetForm = () => {
        setFormData({
            first_name: '',
            middle_name: '',
            last_name: '',
            designation: '',
            dob_of_spouse: '',
            email_id: '',
            mobile_number: '',
            landline_number: '',
            passing_year: '',
            discipline: '',
            blood_group: '',
            postal_address: '',
            pin_code: '',
            state: '',
            country: '',
            spouse_name: '',
            date_of_birth: '',
            introduce_by: '',
            introduce_passing_year: '',
            introduce_discipline: '',
            image: '',
            payment_type: 'Cash',
            cheque_number: '',
            offline_form: '',
            is_agree: true,
            status: 'active'
        });
        setShowForm(false);
        setEditingId(null);
        setSelectedImageFile(null);
        setSelectedFormFile(null);
    };

    const handleToggleForm = () => {
        if (showForm) {
            // If form is shown, close it and reset
            setShowForm(false);
            resetForm();
        } else {
            // If form is hidden, show it with fresh data
            setEditingId(null);
            setFormData({
                first_name: '',
                middle_name: '',
                last_name: '',
                designation: '',
                dob_of_spouse: '',
                email_id: '',
                mobile_number: '',
                landline_number: '',
                passing_year: '',
                discipline: '',
                blood_group: '',
                postal_address: '',
                pin_code: '',
                state: '',
                country: '',
                spouse_name: '',
                date_of_birth: '',
                introduce_by: '',
                introduce_passing_year: '',
                introduce_discipline: '',
                image: '',
                payment_type: 'Cash',
                cheque_number: '',
                offline_form: '',
                is_agree: true,
                status: 'active'
            });
            setShowForm(true);
        }
    };

    // Handle view member details
    const handleView = (member) => {
        setViewMember(member);
        setShowViewModal(true);
    };

    // Download member form as PDF
    const downloadMemberPDF = (member) => {
        const formatDateDisplay = (dateStr) => {
            if (!dateStr || dateStr === 'Invalid date') return '-';
            try {
                const date = new Date(dateStr);
                return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
            } catch { return '-'; }
        };

        const printContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Member Application Form - ${member.first_name} ${member.last_name}</title>
                <style>
                    body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; }
                    h1 { text-align: center; color: #333; border-bottom: 2px solid #333; padding-bottom: 10px; }
                    h2 { color: #555; margin-top: 25px; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
                    table { width: 100%; border-collapse: collapse; margin-top: 10px; }
                    th, td { padding: 10px; text-align: left; border: 1px solid #ddd; }
                    th { background-color: #f5f5f5; width: 30%; font-weight: 600; }
                    .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
                    .logo { font-size: 24px; font-weight: bold; }
                    .date { color: #666; }
                    .status { display: inline-block; padding: 5px 15px; border-radius: 4px; font-weight: bold; }
                    .approved { background: #d4edda; color: #155724; }
                    .pending { background: #fff3cd; color: #856404; }
                    .footer { margin-top: 40px; text-align: center; color: #666; font-size: 12px; }
                    @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
                </style>
            </head>
            <body>
                <div class="header">
                    <div class="logo">B.E. College Ex-Students' Club</div>
                    <div class="date">Generated: ${new Date().toLocaleDateString('en-IN')}</div>
                </div>
                <h1>Member Application Form</h1>
                
                <h2>Personal Information</h2>
                <table>
                    <tr><th>Full Name</th><td>${member.first_name || ''} ${member.middle_name || ''} ${member.last_name || ''}</td></tr>
                    <tr><th>Email</th><td>${member.email_id || '-'}</td></tr>
                    <tr><th>Mobile</th><td>${member.mobile_number || '-'}</td></tr>
                    <tr><th>Date of Birth</th><td>${formatDateDisplay(member.date_of_birth)}</td></tr>
                    <tr><th>Designation</th><td>${member.designation || member.currentDesignation || '-'}</td></tr>
                </table>

                <h2>Academic Information</h2>
                <table>
                    <tr><th>Discipline</th><td>${member.discipline || '-'}</td></tr>
                    <tr><th>Passing Year</th><td>${member.passing_year || member.passingYear || '-'}</td></tr>
                    <tr><th>Company</th><td>${member.company || '-'}</td></tr>
                    <tr><th>Introduced By</th><td>${member.introduce_by || '-'}</td></tr>
                    <tr><th>Introducer Year</th><td>${member.introduce_passing_year || '-'}</td></tr>
                    <tr><th>Introducer Discipline</th><td>${member.introduce_discipline || '-'}</td></tr>
                </table>

                <h2>Address Information</h2>
                <table>
                    <tr><th>Postal Address</th><td>${member.postal_address || member.address || '-'}</td></tr>
                    <tr><th>State</th><td>${member.state || '-'}</td></tr>
                    <tr><th>Country</th><td>${member.country || '-'}</td></tr>
                    <tr><th>Pin Code</th><td>${member.pin_code || '-'}</td></tr>
                </table>

                <h2>Family Information</h2>
                <table>
                    <tr><th>Spouse Name</th><td>${member.spouse_name || '-'}</td></tr>
                    <tr><th>Spouse Date of Birth</th><td>${formatDateDisplay(member.dob_of_spouse)}</td></tr>
                </table>

                <h2>Membership Status</h2>
                <table>
                    <tr><th>Status</th><td><span class="status ${member.is_approved === 1 ? 'approved' : 'pending'}">${member.is_approved === 1 ? 'Approved' : 'Pending Approval'}</span></td></tr>
                    <tr><th>User Code</th><td>${member.user_code || '-'}</td></tr>
                    <tr><th>Application Date</th><td>${formatDateDisplay(member.createdAt)}</td></tr>
                </table>

                <div class="footer">
                    <p>This is a computer generated document from B.E. College Ex-Students' Club Member Management System</p>
                </div>
            </body>
            </html>
        `;

        const printWindow = window.open('', '_blank');
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => { printWindow.print(); }, 500);
    };

    return (
        <>
            <Wrapper>
                <div className="">
                    <div className="row mb-4">
                        <div className="col-12">
                            <div className="d-flex justify-content-between align-items-center">
                                <h3 className="mb-0">Approve New Members</h3>
                            </div>
                        </div>
                    </div>

                    {showForm && (
                        <div className="card mb-4">
                            <div className="card-body">
                                <h5 className="card-title">{editingId ? 'Edit Member' : 'Offline Registration'}</h5>
                                <form onSubmit={handleAddMember}>
                                    <div className="row">
                                        {/* Profile Image Upload */}
                                        <div className="col-12 mb-4">
                                            <label className="form-label">Profile Picture</label>
                                            <div className="d-flex align-items-center gap-3">
                                                <div style={{
                                                    width: 100,
                                                    height: 100,
                                                    borderRadius: '10%',
                                                    overflow: 'hidden',
                                                    background: '#f0f0f0',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    border: '2px dashed #ccc'
                                                }}>
                                                    {formData.image ? (
                                                        <img
                                                            src={formData.image}
                                                            alt="Profile"
                                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                        />
                                                    ) : (
                                                        <span style={{ color: '#999', fontSize: '12px', textAlign: 'center' }}>No Image</span>
                                                    )}
                                                </div>
                                                <div>
                                                    <input
                                                        type="file"
                                                        className="form-control"
                                                        accept="image/*"
                                                        onChange={handleImageChange}
                                                    />
                                                    <small className="text-muted">Select member profile picture</small>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Personal Information */}
                                        <div className="col-12 mb-2">
                                            <h6 className="text-primary border-bottom pb-2">Personal Information</h6>
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">First Name *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="first_name"
                                                value={formData.first_name}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Middle Name</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="middle_name"
                                                value={formData.middle_name}
                                                onChange={handleChange}
                                            />
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Last Name *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="last_name"
                                                value={formData.last_name}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Email ID *</label>
                                            <input
                                                type="email"
                                                className="form-control"
                                                name="email_id"
                                                value={formData.email_id}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Mobile Number *</label>
                                            <input
                                                type="tel"
                                                className="form-control"
                                                name="mobile_number"
                                                value={formData.mobile_number}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Date of Birth *</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                name="date_of_birth"
                                                value={formData.date_of_birth}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Designation</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="designation"
                                                value={formData.designation}
                                                onChange={handleChange}
                                            />
                                        </div>

                                        {/* Academic Information */}
                                        <div className="col-12 mb-2">
                                            <h6 className="text-primary border-bottom pb-2">Academic Information</h6>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Passing Year *</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                name="passing_year"
                                                value={formData.passing_year}
                                                onChange={handleChange}
                                                min="1990"
                                                max={new Date().getFullYear()}
                                                required
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Discipline *</label>
                                            <select
                                                className="form-control"
                                                name="discipline"
                                                value={formData.discipline}
                                                onChange={handleChange}
                                                required
                                            >
                                                <option value="">Select discipline</option>
                                                <option value="Architecture">Architecture</option>
                                                <option value="Civil">Civil</option>
                                                <option value="Electrical">Electrical</option>
                                                <option value="Mechanical">Mechanical</option>
                                                <option value="Metallurgy">Metallurgy</option>
                                                <option value="Mining">Mining</option>
                                            </select>
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Blood Group *</label>
                                            <select
                                                className="form-control"
                                                name="blood_group"
                                                value={formData.blood_group}
                                                onChange={handleChange}
                                                required
                                            >
                                                <option value="">Select blood group</option>
                                                <option value="O+">O+</option>
                                                <option value="O-">O-</option>
                                                <option value="A+">A+</option>
                                                <option value="A-">A-</option>
                                                <option value="B+">B+</option>
                                                <option value="B-">B-</option>
                                                <option value="AB+">AB+</option>
                                                <option value="AB-">AB-</option>
                                            </select>
                                        </div>

                                        {/* Address Information */}
                                        <div className="col-12 mb-2">
                                            <h6 className="text-primary border-bottom pb-2">Address Information</h6>
                                        </div>
                                        <div className="col-12 mb-3">
                                            <label className="form-label">Postal Address</label>
                                            <textarea
                                                className="form-control"
                                                name="postal_address"
                                                value={formData.postal_address}
                                                onChange={handleChange}
                                                rows="2"
                                            />
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Pin Code</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="pin_code"
                                                value={formData.pin_code}
                                                onChange={handleChange}
                                            />
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">State</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="state"
                                                value={formData.state}
                                                onChange={handleChange}
                                            />
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Country</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="country"
                                                value={formData.country}
                                                onChange={handleChange}
                                            />
                                        </div>

                                        {/* Family Information */}
                                        <div className="col-12 mb-2">
                                            <h6 className="text-primary border-bottom pb-2">Family Information</h6>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Spouse Name</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="spouse_name"
                                                value={formData.spouse_name}
                                                onChange={handleChange}
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Spouse Date of Birth</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                name="dob_of_spouse"
                                                value={formData.dob_of_spouse}
                                                onChange={handleChange}
                                            />
                                        </div>

                                        {/* Introducer Information */}
                                        <div className="col-12 mb-2">
                                            <h6 className="text-primary border-bottom pb-2">Introducer Information</h6>
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Introduced By *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="introduce_by"
                                                value={formData.introduce_by}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Introducer Passing Year *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="introduce_passing_year"
                                                value={formData.introduce_passing_year}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Introducer Discipline *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="introduce_discipline"
                                                value={formData.introduce_discipline}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>

                                        {/* Payment Information */}
                                        <div className="col-12 mb-2">
                                            <h6 className="text-primary border-bottom pb-2">Payment Information</h6>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Payment Type *</label>
                                            <select
                                                className="form-control"
                                                name="payment_type"
                                                value={formData.payment_type}
                                                onChange={handleChange}
                                                required
                                            >
                                                <option value="Cash">Cash</option>
                                                <option value="Cheque">Cheque</option>
                                            </select>
                                        </div>
                                        {formData.payment_type === 'Cheque' && (
                                            <div className="col-md-6 mb-3">
                                                <label className="form-label">Cheque Number *</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="cheque_number"
                                                    value={formData.cheque_number}
                                                    onChange={handleChange}
                                                    required
                                                />
                                            </div>
                                        )}

                                        {/* Offline Form Upload */}
                                        <div className="col-12 mb-2">
                                            <h6 className="text-primary border-bottom pb-2">Form Upload</h6>
                                        </div>
                                        <div className="col-12 mb-3">
                                            <label className="form-label">Upload Offline Form (Optional)</label>
                                            <input
                                                type="file"
                                                className="form-control"
                                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                                onChange={handleFormFileChange}
                                            />
                                            <small className="text-muted">
                                                Upload scanned offline membership form (PDF, DOC, DOCX, JPG, PNG)
                                                {formData.offline_form && ` - Selected: ${formData.offline_form}`}
                                            </small>
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Status</label>
                                            <select
                                                className="form-control"
                                                name="status"
                                                value={formData.status}
                                                onChange={handleChange}
                                            >
                                                <option value="active">Active</option>
                                                <option value="inactive">Inactive</option>
                                            </select>
                                        </div>
                                    </div>
                                    <button type="submit" className="btn btn-success">
                                        {editingId ? 'Update' : 'Register'} Member
                                    </button>
                                </form>
                            </div>
                        </div>
                    )}
                    <div className="">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-striped table-hover cus-table">
                                    <thead className="table-dark">
                                        <tr>
                                            <th>Image</th>
                                            <th>Name</th>
                                            <th>Email Id</th>
                                            <th>Phone</th>
                                            <th>Discipline</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredMembers && filteredMembers.length > 0 ? (
                                            filteredMembers.map((member) => member.is_approved !== 1 ? (
                                                <tr key={member.user_code || member.id}>
                                                    <td>
                                                        {member.image ? (
                                                            <img src={member.image} alt="Profile" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: '50%' }} />
                                                        ) : (
                                                            <span style={{ width: 48, height: 48, display: 'inline-block', background: '#eee', borderRadius: '50%' }} />
                                                        )}
                                                    </td>
                                                    <td>{member.first_name} {member.last_name}</td>
                                                    <td>{member.email_id}</td>
                                                    <td>{member.mobile_number}</td>
                                                    <td>{member.discipline || '-'}</td>
                                                    <td>
                                                        <button
                                                            className="btn btn-sm btn-primary me-2"
                                                            onClick={() => handleView(member)}
                                                            title="View Details"
                                                        >
                                                            <i className={'fa fa-eye'} alt="View Details" /> View Details
                                                        </button>
                                                        {
                                                            member.is_approved === 1 ? (
                                                                <span className="badge badge-success">Approved</span>
                                                            ) : (
                                                                <>
                                                                    <button
                                                                        className="btn btn-sm btn-success me-2"
                                                                        onClick={() => handleApproveMember(member)}
                                                                    >
                                                                        <i className='fa fa-check'></i> Approve
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-sm btn-danger"
                                                                        onClick={() => handleDeclineMember(member)}
                                                                    >
                                                                        <i className='fa fa-times'></i> Decline
                                                                    </button>
                                                                </>
                                                            )
                                                        }
                                                    </td>
                                                </tr>
                                            ) : null
                                            )
                                        ) : (
                                            <tr>
                                                <td colSpan="7" className="text-center py-4">
                                                    No members found
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>

                {/* View Member Details Modal */}
                <div className={`modal fade ${showViewModal ? "show d-block modal-white" : ""}`} tabIndex="-1" style={{ background: showViewModal ? "rgba(0,0,0,0.5)" : "transparent" }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Member Details - Pending Approval</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowViewModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                {viewMember && (
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <h6 className="text-muted">Personal Information</h6>
                                            <table className="table table-bordered">
                                                <tbody>
                                                    <tr>
                                                        <th style={{ width: '40%' }}>Name</th>
                                                        <td>{viewMember.first_name} {viewMember.middle_name} {viewMember.last_name}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Email</th>
                                                        <td>{viewMember.email_id || '-'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Mobile</th>
                                                        <td>{viewMember.mobile_number || '-'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Date of Birth</th>
                                                        {
                                                            viewMember.date_of_birth != 'Invalid date' ?
                                                                <td>{viewMember.date_of_birth ? formatDate(viewMember.date_of_birth)?.month + ' ' + formatDate(viewMember.date_of_birth)?.day + ', ' + formatDate(viewMember.date_of_birth)?.year : '-'}</td>
                                                                : <></>
                                                        }
                                                    </tr>
                                                    <tr>
                                                        <th>Designation</th>
                                                        <td>{viewMember.designation || viewMember.currentDesignation || '-'}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <h6 className="text-muted">Academic Information</h6>
                                            <table className="table table-bordered">
                                                <tbody>
                                                    <tr>
                                                        <th style={{ width: '40%' }}>Discipline</th>
                                                        <td>{viewMember.discipline || '-'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Passing Year</th>
                                                        <td>{viewMember.passing_year || viewMember.passingYear || '-'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Company</th>
                                                        <td>{viewMember.company || '-'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Introduced By</th>
                                                        <td>{viewMember.introduce_by || '-'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Introducer Year</th>
                                                        <td>{viewMember.introduce_passing_year || '-'}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="col-md-12 mb-3">
                                            <h6 className="text-muted">Address Information</h6>
                                            <table className="table table-bordered">
                                                <tbody>
                                                    <tr>
                                                        <th style={{ width: '20%' }}>Postal Address</th>
                                                        <td>{viewMember.postal_address || viewMember.address || '-'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>State</th>
                                                        <td>{viewMember.state || '-'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Country</th>
                                                        <td>{viewMember.country || '-'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Pin Code</th>
                                                        <td>{viewMember.pin_code || '-'}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="col-md-12 mb-3">
                                            <h6 className="text-muted">Family Information</h6>
                                            <table className="table table-bordered">
                                                <tbody>
                                                    <tr>
                                                        <th style={{ width: '20%' }}>Spouse Name</th>
                                                        <td>{viewMember.spouse_name || '-'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Spouse Date of Birth</th>

                                                        {
                                                            viewMember.dob_of_spouse != 'Invalid date' ?
                                                                <td>{viewMember.dob_of_spouse ? formatDate(viewMember.dob_of_spouse)?.month + ' ' + formatDate(viewMember.dob_of_spouse)?.day + ', ' + formatDate(viewMember.dob_of_spouse)?.year : '-'}</td>
                                                                : <></>
                                                        }
                                                        {/* {
                                                        viewMember.dob_of_spouse != 'Invalid date' ?

                                                            : <></>
                                                        } */}
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="col-md-12 mb-3">
                                            <h6 className="text-muted">Approval Status</h6>
                                            <table className="table table-bordered">
                                                <tbody>
                                                    <tr>
                                                        <th style={{ width: '20%' }}>Status</th>
                                                        <td>
                                                            <span className={`badge badge-${viewMember.is_approved === 1 ? 'success' : 'warning'}`}>
                                                                {viewMember.is_approved === 1 ? 'Approved' : 'Pending Approval'}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <th>User Code</th>
                                                        <td>{viewMember.user_code || '-'}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-outline-primary me-2"
                                    onClick={() => downloadMemberPDF(viewMember)}
                                >
                                    <i className="fas fa-download me-1"></i> Download PDF
                                </button>
                                {viewMember && viewMember.is_approved !== 1 && (
                                    <>
                                        <button
                                            type="button"
                                            className="btn btn-danger me-2"
                                            onClick={() => handleDeclineMember(viewMember)}
                                        >
                                            <i className="fas fa-times me-1"></i> Decline
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-success"
                                            onClick={() => {
                                                setShowViewModal(false);
                                                handleApproveMember(viewMember);
                                            }}
                                        >Approve Member
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Wrapper>
        </>
    );
}
